import { useMemo, useRef, useState, type ChangeEvent, type DragEvent, type FormEvent } from 'react';
import {
  AlertTriangle,
  CalendarClock,
  ChevronRight,
  Cpu,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Paperclip,
  Pencil,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Card } from '@/components/card';
import { Dialog } from '@/components/dialog';
import { EmptyState } from '@/components/empty-state';
import { Input } from '@/components/input';
import { Menu } from '@/components/menu';
import { Pager } from '@/components/pager';
import { Select } from '@/components/select';
import { Textarea } from '@/components/textarea';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import {
  useClientAssets,
  useCreateAsset,
  useDeleteAsset,
  useDeleteAssetAttachment,
  useUpdateAsset,
  useUploadAssetAttachment,
  type AssetInput,
} from '@/lib/assets';
import { useFormError } from '@/lib/form-error';
import { assetCategories, groupAssetsByCategory } from '@/lib/group-assets';
import { cn } from '@/lib/cn';
import type { AssetAttachmentData, AssetData } from '@/types/api';

const ATTACHMENT_KINDS = [
  { key: 'photo', label: 'Photo' },
  { key: 'receipt', label: 'Receipt' },
  { key: 'warranty', label: 'Warranty' },
  { key: 'other', label: 'Other' },
] as const;
const KIND_LABEL: Record<string, string> = {
  photo: 'Photo',
  receipt: 'Receipt',
  warranty: 'Warranty',
  other: 'Other',
};
/**
 * Per-kind guidance: a soft `accept` nudge for the file picker plus a hint
 * line. Receipts and warranty docs are usually PDFs; photos are images.
 */
const KIND_META: Record<string, { accept?: string; hint: string }> = {
  photo: { accept: 'image/*', hint: 'JPG, PNG, or HEIC — up to 25 MB' },
  receipt: { accept: 'application/pdf,image/*', hint: 'PDF works best — up to 25 MB' },
  warranty: { accept: 'application/pdf,image/*', hint: 'PDF works best — up to 25 MB' },
  other: { hint: 'Any file — up to 25 MB' },
};

const TYPE_OPTIONS: AssetInput['type'][] = ['hardware', 'software', 'network', 'other'];
const STATUS_OPTIONS: NonNullable<AssetInput['status']>[] = [
  'active',
  'retired',
  'in_storage',
  'stolen',
  'missing',
];

const TYPE_LABEL: Record<string, string> = {
  hardware: 'Hardware',
  software: 'Software',
  network: 'Network',
  other: 'Other',
};
const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  retired: 'Retired',
  in_storage: 'In storage',
  stolen: 'Stolen',
  missing: 'Missing',
};

function statusTone(status: string): 'success' | 'neutral' | 'warning' | 'danger' {
  if (status === 'active') return 'success';
  if (status === 'retired' || status === 'in_storage') return 'neutral';
  if (status === 'missing') return 'warning';
  return 'danger';
}

export function ClientAssets({ clientId }: { clientId: number }) {
  const [page, setPage] = useState(1);
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const { data: paginated, isLoading } = useClientAssets(clientId, {
    type: type || undefined,
    status: status || undefined,
    q: q || undefined,
    page,
  });
  const assets = useMemo(() => paginated?.data ?? [], [paginated]);
  const meta = paginated?.meta;
  const editing = editingId != null ? assets.find((a) => a.id === editingId) ?? null : null;
  const groups = useMemo(() => groupAssetsByCategory(assets), [assets]);
  const categorySuggestions = useMemo(() => assetCategories(assets), [assets]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Input
            placeholder="Search assets…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            className="max-w-[220px]"
          />
          <Select
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              setPage(1);
            }}
            className="max-w-[150px]"
          >
            <option value="">All types</option>
            {TYPE_OPTIONS.map((v) => (
              <option key={v} value={v}>{TYPE_LABEL[v]}</option>
            ))}
          </Select>
          <Select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="max-w-[150px]"
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((v) => (
              <option key={v} value={v}>{STATUS_LABEL[v]}</option>
            ))}
          </Select>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" /> Add asset
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
      ) : assets.length === 0 ? (
        <EmptyState
          icon={Cpu}
          title="No assets yet"
          description="Add hardware, software, or network gear under this client."
          action={
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" /> Add asset
            </Button>
          }
        />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--color-border)] bg-[var(--color-surface-muted)]/50 text-left text-[var(--color-foreground-muted)]">
              <tr>
                <th className="px-4 py-2 font-medium">Asset</th>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Warranty</th>
                <th className="px-4 py-2 font-medium">Location</th>
                <th className="px-4 py-2 font-medium text-center">Files</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            {groups.map(([category, groupAssets]) => (
              <AssetGroupBody
                key={category}
                category={category}
                assets={groupAssets}
                clientId={clientId}
                onEdit={setEditingId}
              />
            ))}
          </table>
          {meta ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </Card>
      )}

      <AssetDialog
        open={createOpen}
        clientId={clientId}
        onClose={() => setCreateOpen(false)}
        categorySuggestions={categorySuggestions}
      />
      <AssetDialog
        clientId={clientId}
        asset={editing}
        onClose={() => setEditingId(null)}
        categorySuggestions={categorySuggestions}
      />
    </div>
  );
}

function AssetRow({
  asset,
  clientId,
  onEdit,
}: {
  asset: AssetData;
  clientId: number;
  onEdit: () => void;
}) {
  const del = useDeleteAsset(clientId);
  const confirm = useConfirm();
  const toast = useToast();
  const expired = asset.days_until_warranty_expiry != null && asset.days_until_warranty_expiry < 0;
  const soon =
    asset.days_until_warranty_expiry != null &&
    asset.days_until_warranty_expiry <= 30 &&
    !expired;

  return (
    <tr className="border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-surface-muted)]/50">
      <td className="px-4 py-2.5">
        <div className="font-medium">{asset.name}</div>
        <div className="text-xs text-[var(--color-foreground-subtle)]">
          {[asset.manufacturer, asset.model, asset.identifier].filter(Boolean).join(' · ') || '—'}
        </div>
      </td>
      <td className="px-4 py-2.5 text-[var(--color-foreground-muted)]">
        {TYPE_LABEL[asset.type] ?? asset.type}
        {asset.category ? (
          <span className="text-[var(--color-foreground-subtle)]"> · {asset.category}</span>
        ) : null}
      </td>
      <td className="px-4 py-2.5">
        <Badge tone={statusTone(asset.status)}>{STATUS_LABEL[asset.status] ?? asset.status}</Badge>
      </td>
      <td className="px-4 py-2.5">
        {asset.warranty_expires_at ? (
          <Badge tone={expired ? 'danger' : soon ? 'warning' : 'neutral'}>
            {expired ? (
              <AlertTriangle className="mr-1 size-3" />
            ) : (
              <CalendarClock className="mr-1 size-3" />
            )}
            {expired
              ? `Expired ${Math.abs(asset.days_until_warranty_expiry!)} d ago`
              : `${asset.days_until_warranty_expiry} d`}
          </Badge>
        ) : (
          <span className="text-xs text-[var(--color-foreground-subtle)]">—</span>
        )}
      </td>
      <td className="px-4 py-2.5 text-[var(--color-foreground-muted)]">
        {asset.location || <span className="text-xs text-[var(--color-foreground-subtle)]">—</span>}
      </td>
      <td className="px-4 py-2.5 text-center">
        {asset.attachments_count > 0 ? (
          <span className="inline-flex items-center gap-1 text-xs text-[var(--color-foreground-muted)]">
            <Paperclip className="size-3" />
            {asset.attachments_count}
          </span>
        ) : (
          <span className="text-xs text-[var(--color-foreground-subtle)]">—</span>
        )}
      </td>
      <td className="whitespace-nowrap px-4 py-2.5 text-right">
        <Menu
          items={[
            { key: 'edit', label: 'Edit', icon: Pencil, onSelect: onEdit },
            {
              key: 'delete',
              label: 'Delete',
              icon: Trash2,
              danger: true,
              onSelect: async () => {
                const ok = await confirm({
                  title: `Delete ${asset.name}?`,
                  body: 'The asset record will be removed. You can re-add it later if needed.',
                  confirmLabel: 'Delete asset',
                  tone: 'danger',
                });
                if (ok) {
                  await del.mutateAsync(asset.id);
                  toast.success(`${asset.name} deleted`);
                }
              },
            },
          ]}
        />
      </td>
    </tr>
  );
}

type DialogTab = 'details' | 'attachments';

function AssetGroupBody({
  category,
  assets,
  clientId,
  onEdit,
}: {
  category: string;
  assets: AssetData[];
  clientId: number;
  onEdit: (id: number) => void;
}) {
  const [open, setOpen] = useState(true);

  return (
    <tbody>
      <tr className="border-b border-[var(--color-border)] bg-[var(--color-surface-muted)]/40">
        <td colSpan={7} className="px-3 py-1.5">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className="flex items-center gap-2 text-xs font-medium text-[var(--color-foreground)]"
          >
            <ChevronRight
              className={cn('size-3.5 text-[var(--color-foreground-muted)] transition-transform', open && 'rotate-90')}
            />
            {category}
            <span className="rounded-full bg-[var(--color-surface)] px-1.5 text-[10px] tabular-nums text-[var(--color-foreground-muted)]">
              {assets.length}
            </span>
          </button>
        </td>
      </tr>
      {open
        ? assets.map((a) => (
            <AssetRow key={a.id} asset={a} clientId={clientId} onEdit={() => onEdit(a.id)} />
          ))
        : null}
    </tbody>
  );
}

function AssetDialog({
  clientId,
  asset,
  open,
  onClose,
  categorySuggestions = [],
}: {
  clientId: number;
  asset?: AssetData | null;
  open?: boolean;
  onClose: () => void;
  categorySuggestions?: string[];
}) {
  const isEdit = !!asset;
  const isOpen = isEdit ? asset != null : !!open;
  const create = useCreateAsset(clientId);
  const update = useUpdateAsset(clientId);
  const { error, wrap } = useFormError();

  const [seeded, setSeeded] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState<AssetInput>(initial());
  const [tab, setTab] = useState<DialogTab>('details');

  // Seed when dialog opens with a new asset (edit) or first opens (create).
  const seedKey: number | 'new' | null = asset ? asset.id : isOpen ? 'new' : null;
  if (seedKey !== null && seeded !== seedKey) {
    setForm(asset ? fromAsset(asset) : initial());
    setTab('details');
    setSeeded(seedKey);
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const ok = await wrap(() =>
      asset
        ? update.mutateAsync({ id: asset.id, patch: form })
        : create.mutateAsync(form),
    );
    if (ok) {
      setSeeded(null);
      onClose();
    }
  }

  function handleClose() {
    setSeeded(null);
    onClose();
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      title={isEdit ? 'Edit asset' : 'Add asset'}
      size="lg"
    >
      {isEdit && asset ? (
        <div className="mb-4 flex items-center gap-0.5 border-b border-[var(--color-border)]">
          <TabButton active={tab === 'details'} onClick={() => setTab('details')}>
            Details
          </TabButton>
          <TabButton
            active={tab === 'attachments'}
            onClick={() => setTab('attachments')}
          >
            Attachments
            {asset.attachments_count > 0 ? (
              <span className="ml-1 rounded bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--color-foreground-muted)]">
                {asset.attachments_count}
              </span>
            ) : null}
          </TabButton>
        </div>
      ) : null}
      {tab === 'attachments' && asset ? (
        <AttachmentsPanel asset={asset} clientId={clientId} />
      ) : null}
      <form
        onSubmit={submit}
        className={cn('space-y-4', tab !== 'details' && 'hidden')}
      >
        <div className="grid grid-cols-2 gap-3">
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Name</span>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Type</span>
            <Select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value as AssetInput['type'] })}
            >
              {TYPE_OPTIONS.map((v) => (
                <option key={v} value={v}>{TYPE_LABEL[v]}</option>
              ))}
            </Select>
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Category</span>
            <Input
              placeholder="e.g. Networking, Communications"
              list="asset-category-suggestions"
              value={form.category ?? ''}
              onChange={(e) => setForm({ ...form, category: e.target.value || null })}
            />
            {categorySuggestions.length > 0 ? (
              <datalist id="asset-category-suggestions">
                {categorySuggestions.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            ) : null}
          </label>
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Status</span>
            <Select
              value={form.status ?? 'active'}
              onChange={(e) =>
                setForm({ ...form, status: e.target.value as AssetInput['status'] })
              }
            >
              {STATUS_OPTIONS.map((v) => (
                <option key={v} value={v}>{STATUS_LABEL[v]}</option>
              ))}
            </Select>
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Manufacturer</span>
            <Input
              value={form.manufacturer ?? ''}
              onChange={(e) => setForm({ ...form, manufacturer: e.target.value || null })}
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Model</span>
            <Input
              value={form.model ?? ''}
              onChange={(e) => setForm({ ...form, model: e.target.value || null })}
            />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Serial / identifier</span>
            <Input
              value={form.identifier ?? ''}
              onChange={(e) => setForm({ ...form, identifier: e.target.value || null })}
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Location</span>
            <Input
              value={form.location ?? ''}
              onChange={(e) => setForm({ ...form, location: e.target.value || null })}
            />
          </label>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Purchased</span>
            <Input
              type="date"
              value={form.purchased_at ?? ''}
              onChange={(e) => setForm({ ...form, purchased_at: e.target.value || null })}
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Warranty expires</span>
            <Input
              type="date"
              value={form.warranty_expires_at ?? ''}
              onChange={(e) => setForm({ ...form, warranty_expires_at: e.target.value || null })}
            />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Cost ($)</span>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={form.cost_cents != null ? (form.cost_cents / 100).toString() : ''}
              onChange={(e) => {
                const v = e.target.value;
                setForm({
                  ...form,
                  cost_cents: v === '' ? null : Math.round(parseFloat(v) * 100),
                });
              }}
            />
          </label>
        </div>
        <label className="block space-y-1 text-sm">
          <span className="font-medium text-[var(--color-foreground-muted)]">Notes</span>
          <Textarea
            rows={3}
            value={form.notes ?? ''}
            onChange={(e) => setForm({ ...form, notes: e.target.value || null })}
          />
        </label>
        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={handleClose}>Cancel</Button>
          <Button type="submit" disabled={create.isPending || update.isPending}>
            {(create.isPending || update.isPending) ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
        active
          ? 'border-[var(--color-primary)] text-[var(--color-foreground)]'
          : 'border-transparent text-[var(--color-foreground-muted)] hover:text-[var(--color-foreground)]',
      )}
    >
      {children}
    </button>
  );
}

function AttachmentsPanel({
  asset,
  clientId,
}: {
  asset: AssetData;
  clientId: number;
}) {
  const upload = useUploadAssetAttachment(clientId, asset.id);
  const del = useDeleteAssetAttachment(clientId, asset.id);
  const confirm = useConfirm();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [kind, setKind] = useState('photo');

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      try {
        await upload.mutateAsync({ file, kind });
        toast.success(`Uploaded ${file.name}`);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
  }

  const grouped = ATTACHMENT_KINDS.map((k) => ({
    ...k,
    items: asset.attachments.filter((a) => (a.kind || 'other') === k.key),
  })).filter((g) => g.items.length > 0);

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setOver(false);
    handleFiles(e.dataTransfer.files);
  }

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    handleFiles(e.target.files);
    e.target.value = '';
  }

  async function removeAttachment(att: AssetAttachmentData) {
    const ok = await confirm({
      title: `Delete ${att.file_name}?`,
      body: 'The file will be permanently removed.',
      confirmLabel: 'Delete file',
      tone: 'danger',
    });
    if (ok) {
      await del.mutateAsync(att.id);
      toast.success(`${att.file_name} deleted`);
    }
  }

  return (
    <div className="space-y-3">
      {/* Choose what kind of file you're adding. */}
      <div
        role="radiogroup"
        aria-label="Attachment kind"
        className="inline-flex rounded-md border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-0.5"
      >
        {ATTACHMENT_KINDS.map((k) => {
          const active = kind === k.key;
          return (
            <button
              key={k.key}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setKind(k.key)}
              className={cn(
                'rounded px-2.5 py-1 text-xs font-medium transition-colors',
                active
                  ? 'bg-[var(--color-surface)] text-[var(--color-foreground)] shadow-sm'
                  : 'text-[var(--color-foreground-muted)] hover:text-[var(--color-foreground)]',
              )}
            >
              {k.label}
            </button>
          );
        })}
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-6 text-center transition-colors',
          over && 'border-[var(--color-primary)] bg-[var(--color-surface-muted)]',
          upload.isPending && 'opacity-60',
        )}
      >
        <Upload className="size-5 text-[var(--color-foreground-muted)]" />
        <div className="text-sm font-medium">
          {upload.isPending
            ? 'Uploading…'
            : `Drop ${KIND_LABEL[kind].toLowerCase()} files, or click to choose`}
        </div>
        <div className="text-xs text-[var(--color-foreground-subtle)]">
          {KIND_META[kind].hint}
        </div>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          multiple
          accept={KIND_META[kind].accept}
          onChange={onPick}
        />
      </div>

      {asset.attachments.length === 0 ? (
        <p className="px-1 text-xs text-[var(--color-foreground-subtle)]">
          No attachments yet. Photos, receipts, and warranty docs all live here.
        </p>
      ) : (
        <div className="space-y-3">
          {grouped.map((group) => (
            <div key={group.key}>
              <div className="mb-1 px-1 text-[11px] font-medium uppercase tracking-wide text-[var(--color-foreground-muted)]">
                {group.label} · {group.items.length}
              </div>
              <ul className="divide-y divide-[var(--color-border)] rounded-md border border-[var(--color-border)]">
                {group.items.map((att) => (
                  <AttachmentRow key={att.id} attachment={att} onDelete={() => removeAttachment(att)} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AttachmentRow({
  attachment,
  onDelete,
}: {
  attachment: AssetAttachmentData;
  onDelete: () => void;
}) {
  const Icon = attachment.is_image ? ImageIcon : FileText;
  return (
    <li className="flex items-center gap-3 px-3 py-2.5 text-sm">
      <Icon className="size-4 shrink-0 text-[var(--color-foreground-muted)]" />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{attachment.file_name}</div>
        <div className="text-xs text-[var(--color-foreground-subtle)]">
          {(attachment.file_size / 1024).toFixed(1)} KB
        </div>
      </div>
      <a
        href={attachment.url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-[var(--color-foreground-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-foreground)]"
      >
        Open <ExternalLink className="size-3" />
      </a>
      <button
        type="button"
        onClick={onDelete}
        aria-label={`Delete ${attachment.file_name}`}
        className="inline-flex size-7 items-center justify-center rounded text-[var(--color-foreground-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-danger)]"
      >
        <Trash2 className="size-3.5" />
      </button>
    </li>
  );
}

function initial(): AssetInput {
  return {
    type: 'hardware',
    category: null,
    name: '',
    identifier: null,
    manufacturer: null,
    model: null,
    location: null,
    assigned_to_contact_id: null,
    purchased_at: null,
    warranty_expires_at: null,
    cost_cents: null,
    status: 'active',
    notes: null,
    tags: [],
  };
}

function fromAsset(a: AssetData): AssetInput {
  return {
    type: a.type as AssetInput['type'],
    category: a.category,
    name: a.name,
    identifier: a.identifier,
    manufacturer: a.manufacturer,
    model: a.model,
    location: a.location,
    assigned_to_contact_id: a.assigned_to_contact_id,
    purchased_at: a.purchased_at,
    warranty_expires_at: a.warranty_expires_at,
    cost_cents: a.cost_cents,
    status: a.status as AssetInput['status'],
    notes: a.notes,
    tags: a.tags,
  };
}
